import {
    BuildingOfficeIcon,
    GlobeAltIcon,
    InformationCircleIcon,
    MapPinIcon,
    UserIcon,
} from "@heroicons/react/24/outline";
import { Input, Textarea, Tooltip } from "@heroui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UpdateProfileUIProps } from "../../../types/updateProfileProps";

type SearchableLocationFieldProps = {
    label: React.ReactNode;
    tooltipContent?: string;
    placeholder: string;
    value: string;
    options: string[];
    icon: React.ReactNode;
    errorMessage?: string;
    name: "nationality" | "residenceCountry";
    onValueChange: (value: string) => void;
    onSelected?: (value: string) => void;
};

const SearchableLocationField: React.FC<SearchableLocationFieldProps> = ({
    label,
    tooltipContent,
    placeholder,
    value,
    options,
    icon,
    errorMessage,
    name,
    onValueChange,
    onSelected,
}) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (wrapperRef.current && target && !wrapperRef.current.contains(target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = useMemo(() => {
        const q = value.trim().toLowerCase();
        if (!q) return options;
        return options.filter((item) => item.toLowerCase().includes(q));
    }, [options, value]);

    const labelWithTooltip = tooltipContent ? (
        <div className="flex items-center gap-1">
            {label}
            <Tooltip className="w-[350px]" content={tooltipContent}>
                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
            </Tooltip>
        </div>
    ) : (
        label
    );

    return (
        <div ref={wrapperRef} className="relative w-full">
            <Input
                name={name}
                value={value}
                label={labelWithTooltip}
                labelPlacement="outside"
                placeholder={placeholder}
                onValueChange={(nextValue) => {
                    onValueChange(nextValue);
                    setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => {
                    window.setTimeout(() => {
                        setDropdownOpen(false);
                    }, 150);
                }}
                isInvalid={!!errorMessage}
                errorMessage={errorMessage}
                size="sm"
                radius="lg"
                className="w-full"
                classNames={{
                    inputWrapper:
                        "border-gray-200 hover:border-gray-300 focus-within:border-[#1E3A8A]",
                    input: "text-xs text-gray-600",
                }}
                startContent={icon}
            />

            {dropdownOpen && (
                <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-default-200 bg-white shadow-lg">
                    <div className="max-h-48 overflow-auto">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((item) => (
                                <button
                                    key={item}
                                    type="button"
                                    className="w-full px-3 py-2 text-left text-xs hover:bg-default-100 text-gray-600"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        onValueChange(item);
                                        onSelected?.(item);
                                        setDropdownOpen(false);
                                    }}
                                >
                                    {item}
                                </button>
                            ))
                        ) : (
                            <div className="px-3 py-2 text-xs text-default-500">
                                No results
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const BasicInfoSection: React.FC<UpdateProfileUIProps> = ({
    countries,
    validationErrors,
    formData,
    member,
    handleInputChange,
    handleFileChange,
    previewUrl,
}) => {
    const { t } = useTranslation("updateProfile");

    const updateFieldValue = (name: string, value: string) => {
        handleInputChange({
            target: {
                name,
                value,
            },
        } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
        <div className="grid grid-cols-3 gap-4 p-4">
            <div className="relative w-full">
                <Input
                    name="firstName"
                    label={t("basicInfo.firstName")}
                    value={formData.firstName}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.firstName}
                    errorMessage={validationErrors.firstName}
                    labelPlacement="outside"
                    maxLength={30}
                    size="sm"
                    className="w-full"
                    classNames={{
                        inputWrapper:
                            "border-gray-200 hover:border-gray-300 focus-within:border-[#1E3A8A]",
                    }}
                    startContent={<UserIcon className="w-4 h-4 text-gray-400" />}
                />
            </div>

        
            <div className="relative w-full">
                <Input
                    id="lastName"
                    name="lastName"
                    label={t("basicInfo.lastName")}
                    labelPlacement="outside"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.lastName}
                    errorMessage={validationErrors.lastName}
                    maxLength={25}
                    size="sm"
                    className="w-full"
                    classNames={{
                        inputWrapper:
                            "border-gray-200 hover:border-gray-300 focus-within:border-[#1E3A8A]",
                    }}
                    startContent={<UserIcon className="w-4 h-4 text-gray-400" />}
                />
            </div>

            {member.profileType === "ORGANIZATION" && (
                <div className="relative w-full">
                    <Input
                        name="organizationName"
                        label={t("managerInfoSection.organizationName")}
                        labelPlacement="outside"
                        value={formData.organizationName}
                        onChange={handleInputChange}
                        isInvalid={!!validationErrors.organizationName}
                        errorMessage={validationErrors.organizationName}
                        maxLength={40}
                        size="sm"
                        className="w-full"
                        classNames={{
                            inputWrapper:
                                "border-gray-200 hover:border-gray-300 focus-within:border-[#1E3A8A]",
                        }}
                        startContent={
                            <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
                        }
                    />
                </div>
            )}

           
            <div className="relative w-full">
                <Input
                    id="phone"
                    name="phone"
                    label={t("basicInfo.phone")}
                    labelPlacement="outside"
                    value={formData.phone}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.phone}
                    errorMessage={validationErrors.phone}
                    size="sm"
                    className="w-full"
                    classNames={{
                        inputWrapper:
                            "border-gray-200 hover:border-gray-300 focus-within:border-[#1E3A8A]",
                    }}
                    startContent={<UserIcon className="w-4 h-4 text-gray-400" />}
                />
            </div>

            
            <SearchableLocationField
                label={t("basicInfo.nationality")}
                tooltipContent={t("basicInfo.nationalityTooltip")}
                name="nationality"
                placeholder={t("basicInfo.nationality")}
                value={formData.nationality}
                options={countries}
                icon={<GlobeAltIcon className="w-4 h-4 text-gray-400" />}
                errorMessage={validationErrors.nationality}
                onValueChange={(value) => {
                    updateFieldValue("nationality", value);
                }}
            />

            
            <SearchableLocationField
                label={t("basicInfo.residenceCountry")}
                tooltipContent={t("basicInfo.residenceCountryTooltip")}
                name="residenceCountry"
                placeholder={t("basicInfo.residenceCountryPlaceholder")}
                value={formData.residenceCountry}
                options={countries}
                icon={<MapPinIcon className="w-4 h-4 text-gray-400" />}
                errorMessage={validationErrors.residenceCountry}
                onValueChange={(value) => {
                    updateFieldValue("residenceCountry", value);
                }}
            />

         
            <div className="col-span-3">
                <label className="block text-sm font-light text-gray-700 mb-1">
                    {t("basicInfo.profileImage")}
                </label>
                <div className="flex items-center gap-4">
                    {previewUrl ? (
                        <img
                            src={previewUrl}
                            alt="Preview"
                            className="h-16 w-16 rounded-full object-cover border"
                        />
                    ) : member.profileImage ? (
                        <img
                            src={member.profileImage}
                            alt="Profile"
                            className="h-16 w-16 rounded-full object-cover border"
                        />
                    ) : (
                        <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                            ?
                        </div>
                    )}

                    <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleFileChange}
                        className="text-sm text-gray-600 file:mr-4 file:py-1 file:px-2 
                            file:rounded file:border-0 file:text-sm 
                            file:bg-gray-100 file:text-gray-600 hover:file:bg-gray-200"
                    />
                </div>
                {validationErrors.profileImage && (
                    <p className="text-red-600 text-sm mt-1">
                        {validationErrors.profileImage}
                    </p>
                )}
            </div>

        
            <Textarea
                label={
                    <div className="flex items-center gap-1">
                        {t("basicInfo.bio")}
                        <Tooltip
                            className="w-[350px]"
                            placement="right"
                            content={t("basicInfo.bioTooltip")}
                        >
                            <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                        </Tooltip>
                    </div>
                }
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                isInvalid={!!validationErrors.bio}
                errorMessage={validationErrors.bio}
                labelPlacement="outside"
                className="col-span-3"
                maxLength={1000}
                classNames={{
                    inputWrapper:
                        "border-gray-200 hover:border-gray-300 focus-within:border-[#1E3A8A]",
                }}
            />
        </div>
    );
};

export default BasicInfoSection;
